import { createRouter, createWebHistory } from 'vue-router'

// External libraries
import qs from 'qs'

// State management
import { useAuthStore } from '@shared/store/auth'

// Config
import { env } from '@shared/config/env'

// Utils
import { checkPermissions, missingScopes } from '@shared/utils/permissions'
import { usePosthog } from '@shared/plugins/posthog'

// Routes
import { isUnifiedCommerce, isKeycloackEnabled } from '@shared/constants'
import {
  discardUnboundSupportLoginResponse,
  stripLoginResponseFromUrl
} from '@shared/plugins/keycloak'
import thRoutes from '@th/router/th-router.js'
import uzRoutes from '@uz/router/uz-router.js'
import { useRouteQueryHistorySetup } from '@shared/composables/use-route-query-history'
import { handleStaleAssetFailure } from '@shared/utils/stale-asset-recovery'

// Before `createWebHistory`, which reads the address once on creation and
// replays it on the first navigation: a later cleanup would simply reappear.
if (isUnifiedCommerce() && isKeycloackEnabled()) {
  stripLoginResponseFromUrl()
  discardUnboundSupportLoginResponse()
}

const router = createRouter({
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  history: createWebHistory(env.baseUrl),
  routes: isUnifiedCommerce() ? uzRoutes : thRoutes
})

useRouteQueryHistorySetup(router)

router.onError(function (err) {
  if (import.meta.env.DEV) {
    // In dev environments, do not redirect to error pages, as the application will lose
    // the UI context and also refreshing will not work to test out a fix.
    // eslint-disable-next-line no-console
    return console.error(err)
  }

  const recovery = handleStaleAssetFailure(err)
  if (recovery === 'reload' || recovery === 'reload-duplicate') {
    return
  }

  router.push({
    name: err.status ? err.status.toString() : '500',
    params: { error: err }
  })
})

router.beforeResolve(async (routeTo, routeFrom) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      if (route?.meta?.scopes) {
        const isParent = !!route?.children?.length
        const redirect = await resolvePermissionsRedirect(route.meta, isParent)
        if (redirect) {
          return redirect
        }
      }
      if (route?.meta?.rolloutFlag) {
        // Checked after the scopes check on purpose: that awaits the me data, which is what
        // identifies the user in Posthog and therefore triggers the feature flag fetch.
        const isEnabled = await usePosthog().isFeatureEnabledWhenReady(
          route.meta.rolloutFlag
        )

        if (!isEnabled) {
          return { path: '/404' }
        }
      }
      // If a `beforeResolve` hook is defined, call it with
      // the same arguments as the `beforeEnter` hook. Anything it returns is
      // a navigation result and is handed straight back to Vue Router.
      if (route.meta && route.meta.beforeResolve) {
        const result = await route.meta.beforeResolve(routeTo, routeFrom)
        if (result !== undefined) {
          return result
        }
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    // Abort rather than let a half-resolved route through.
    return false
  }
})

async function resolvePermissionsRedirect({ scopes }, isParent = false) {
  const authStore = useAuthStore()
  await authStore.waitThInitializated()

  const routeRequiresScopes = scopes?.length && !isParent
  if (routeRequiresScopes && authStore.isAuthenticated) {
    await authStore.waitForMeData()
  }

  const role = authStore.role
  const userScopes = authStore.scopes
  if (!isParent && !checkPermissions({ scopes }, role, userScopes)) {
    const params = qs.stringify(missingScopes(scopes, userScopes), {
      addQueryPrefix: true
    })
    return { path: `/403${params}` }
  }
  return null
}

// Prevent vue-router navigation guard from throwing errors
// Solution based on a comment by vue-router's main maintainer
// Source: https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
/*
const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace
Router.prototype.push = routerFailureHandling(originalPush)
Router.prototype.replace = routerFailureHandling(originalReplace)


function routerFailureHandling(originalMethod) {
  return function (location, onResolve, onReject) {
    if (onResolve || onReject) {
      return originalMethod.call(this, location, onResolve, onReject)
    }
    return originalMethod.call(this, location).catch((err) => {
      if (Router.isNavigationFailure(err)) {
        if (env.nodeEnv !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('NavigationFailure', err)
        }
        // resolve err
        return err
      }
      // rethrow error
      return Promise.reject(err)
    })
  }
}
*/
export default router
